// Shared results panel for all three eDOS Search-By pages. Top strip
// shows the result count + sort dropdown. Body is a 6-column grid:
// Code · Test (with aliases + dept pill) · TAT · Schedule · Sample
// Cutoff · MRP. Clicking any row navigates the partner to the test
// detail page via the `onSelectTest` callback supplied by the page.
//
// Same panel is rendered by EdosTests / EdosDepartments / EdosSpeciality
// — only the picker on top of the page changes.
import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { MP, MONO, DISPLAY } from '../medical/shared';
import { sortTests } from './edosData';

const COLS = '110px 1fr 80px 130px 110px 110px 28px';

const SORT_OPTIONS = [
  { id: 'name', label: 'Name (A → Z)' },
  { id: 'code', label: 'Code' },
  { id: 'dept', label: 'Department' },
  { id: 'tat',  label: 'TAT (Fastest)' },
  { id: 'mrp',  label: 'MRP (Lowest)' },
];

const HeaderCell = ({ children, align = 'left' }) => (
  <Typography
    sx={{
      fontFamily: MONO,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.16em',
      textTransform: 'uppercase',
      color: MP.textMuted,
      textAlign: align,
    }}
  >
    {children}
  </Typography>
);

const Row = ({ test, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'grid',
      gridTemplateColumns: COLS,
      alignItems: 'center',
      gap: 2,
      px: 2,
      py: 1.75,
      borderBottom: `1px solid ${MP.border}`,
      cursor: 'pointer',
      transition: 'background 0.14s',
      '&:hover': {
        bgcolor: MP.tealLight,
        '& .row-chevron': { color: MP.tealDark, transform: 'translateX(2px)' },
      },
    }}
  >
    {/* Code */}
    <Typography
      sx={{
        fontFamily: MONO,
        fontSize: 11.5,
        fontWeight: 700,
        color: MP.tealDark,
        letterSpacing: '0.04em',
      }}
    >
      {test.code}
    </Typography>

    {/* Name + aliases + dept pill */}
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: MP.dark, lineHeight: 1.25 }}>
          {test.name}
        </Typography>
        <Box
          component="span"
          sx={{
            fontFamily: MONO,
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: MP.tealDark,
            bgcolor: MP.tealLight,
            border: `1px solid ${MP.tealMid}`,
            borderRadius: '20px',
            px: 0.875,
            py: 0.125,
          }}
        >
          {test.dept}
        </Box>
      </Stack>
      {test.aliases?.length > 0 && (
        <Typography
          sx={{
            fontSize: 11,
            color: MP.textMuted,
            mt: 0.25,
            fontStyle: 'italic',
          }}
        >
          aka {test.aliases.join(' · ')}
        </Typography>
      )}
    </Box>

    {/* TAT */}
    <Typography sx={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: MP.dark, textAlign: 'center' }}>
      {test.tat}
    </Typography>

    {/* Schedule */}
    <Typography sx={{ fontSize: 12, color: MP.text }}>
      {test.schedule}
    </Typography>

    {/* Sample Cutoff */}
    <Typography sx={{ fontFamily: MONO, fontSize: 11.5, color: MP.textMuted }}>
      {test.cutoff}
    </Typography>

    {/* MRP only — L2L partner price was dropped from the portal
        listing per product call (the public-facing catalogue should
        only surface the rack rate). The l2l_price field still lives
        on each record for downstream pricing logic, just not shown
        here. */}
    <Box sx={{ textAlign: 'right' }}>
      <Typography sx={{ fontFamily: DISPLAY, fontSize: 14, fontWeight: 700, color: MP.dark, lineHeight: 1 }}>
        ₹{test.mrp}
      </Typography>
    </Box>

    {/* Chevron — visual hint that the row navigates */}
    <KeyboardArrowRightIcon
      className="row-chevron"
      sx={{
        fontSize: 18,
        color: MP.border,
        transition: 'all 0.14s',
        justifySelf: 'end',
      }}
    />
  </Box>
);

const EdosResultsList = ({
  tests,
  onSelectTest,
  emptyTitle = 'No tests match',
  emptySub = 'Try a different search term or filter.',
  // API integration props — all optional so static callers keep working.
  loading = false,
  error = null,
  pagination = null,            // { current_page, last_page, total }
  onPageChange = null,          // (nextPage) => void
  totalLabel = null,            // override the count strip (e.g. "of 1,247 in catalogue")
}) => {
  const [sortBy, setSortBy] = useState('name');
  const sorted = useMemo(() => sortTests(tests, sortBy), [tests, sortBy]);
  const totalCount = pagination?.total ?? tests.length;

  return (
    <Box
      sx={{
        bgcolor: MP.white,
        border: `1px solid ${MP.border}`,
        borderRadius: '6px',
        boxShadow: MP.shadow,
      }}
    >
      {/* Top strip — count + sort */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${MP.border}` }}
      >
        <Stack direction="row" spacing={1.25} alignItems="baseline">
          <Typography sx={{ fontFamily: DISPLAY, fontSize: 20, fontWeight: 700, letterSpacing: '-.01em', color: MP.dark }}>
            {totalCount}
          </Typography>
          <Typography sx={{ fontFamily: MONO, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: MP.textMuted }}>
            {totalLabel || (totalCount === 1 ? 'Test' : 'Tests')}
          </Typography>
          {loading && (
            <Typography sx={{ fontFamily: MONO, fontSize: 10, color: MP.tealDark, fontWeight: 700, letterSpacing: '0.12em' }}>
              · LOADING…
            </Typography>
          )}
        </Stack>

        <Stack direction="row" spacing={1.25} alignItems="center">
          <Typography sx={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: MP.textMuted }}>
            Sort
          </Typography>
          <Box
            component="select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            sx={{
              fontFamily: MONO,
              fontSize: 12,
              fontWeight: 700,
              color: MP.dark,
              bgcolor: MP.white,
              border: `1px solid ${MP.border}`,
              borderRadius: '6px',
              boxShadow: MP.shadow,
              px: 1.25,
              py: 0.75,
              outline: 'none',
              cursor: 'pointer',
              '&:focus': { borderColor: MP.teal },
            }}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </Box>
        </Stack>
      </Stack>

      {/* Header row */}
      {tests.length > 0 && (
        <Box
          sx={{
            display: { xs: 'none', md: 'grid' },
            gridTemplateColumns: COLS,
            gap: 2,
            px: 2,
            py: 1.25,
            borderBottom: `1px solid ${MP.border}`,
            bgcolor: MP.bg,
          }}
        >
          <HeaderCell>Code</HeaderCell>
          <HeaderCell>Test</HeaderCell>
          <HeaderCell align="center">TAT</HeaderCell>
          <HeaderCell>Schedule</HeaderCell>
          <HeaderCell>Sample Cutoff</HeaderCell>
          <HeaderCell align="right">MRP</HeaderCell>
          <Box />
        </Box>
      )}

      {/* Rows */}
      {sorted.map((t) => (
        <Row key={t.code} test={t} onClick={() => onSelectTest && onSelectTest(t)} />
      ))}

      {/* Loading skeleton — 3 ghost rows so the layout doesn't jump
          when the API resolves. Only renders during the very first
          fetch (when there are no rows yet). */}
      {loading && tests.length === 0 && (
        <Box>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                display: 'grid',
                gridTemplateColumns: COLS,
                gap: 2,
                px: 2,
                py: 1.75,
                borderBottom: `1px solid ${MP.border}`,
                opacity: 0.5,
                '@keyframes lp-pulse': { '0%, 100%': { opacity: 0.35 }, '50%': { opacity: 0.65 } },
                animation: 'lp-pulse 1.4s ease-in-out infinite',
              }}
            >
              <Box sx={{ height: 12, bgcolor: MP.border, borderRadius: 0.5 }} />
              <Box sx={{ height: 14, bgcolor: MP.border, borderRadius: 0.5, maxWidth: '70%' }} />
              <Box sx={{ height: 12, bgcolor: MP.border, borderRadius: 0.5 }} />
              <Box sx={{ height: 12, bgcolor: MP.border, borderRadius: 0.5 }} />
              <Box sx={{ height: 12, bgcolor: MP.border, borderRadius: 0.5 }} />
              <Box sx={{ height: 14, bgcolor: MP.border, borderRadius: 0.5 }} />
              <Box />
            </Box>
          ))}
        </Box>
      )}

      {/* Error state — surfaces network/server failures cleanly. */}
      {error && tests.length === 0 && !loading && (
        <Box sx={{ px: 4, py: 6, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: DISPLAY, fontSize: 18, fontWeight: 700, color: MP.red, mb: 0.75 }}>
            Couldn't load tests
          </Typography>
          <Typography sx={{ fontSize: 13, color: MP.textMuted }}>
            {error?.data?.message || error?.error || 'Network error — please retry.'}
          </Typography>
        </Box>
      )}

      {/* Empty state */}
      {!loading && !error && tests.length === 0 && (
        <Box sx={{ px: 4, py: 6, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: DISPLAY, fontSize: 18, fontWeight: 700, color: MP.dark, mb: 0.75 }}>
            {emptyTitle}
          </Typography>
          <Typography sx={{ fontSize: 13, color: MP.textMuted }}>
            {emptySub}
          </Typography>
        </Box>
      )}

      {/* Pagination — only renders when the caller wires onPageChange.
          Shows current page / total pages and Prev/Next buttons. */}
      {pagination && pagination.last_page > 1 && onPageChange && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2.5, py: 1.75, borderTop: `1px solid ${MP.border}`, bgcolor: MP.bg }}
        >
          <Typography sx={{ fontFamily: MONO, fontSize: 11, color: MP.textMuted, fontWeight: 700, letterSpacing: '0.08em' }}>
            Page {pagination.current_page} of {pagination.last_page} · {pagination.total} total
          </Typography>
          <Stack direction="row" spacing={1}>
            <Box
              onClick={() => pagination.current_page > 1 && onPageChange(pagination.current_page - 1)}
              sx={{
                fontFamily: MONO,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: pagination.current_page > 1 ? MP.tealDark : MP.textMuted,
                bgcolor: MP.white,
                border: `1px solid ${pagination.current_page > 1 ? MP.tealMid : MP.border}`,
                borderRadius: '6px',
                px: 1.5,
                py: 0.75,
                cursor: pagination.current_page > 1 ? 'pointer' : 'not-allowed',
                opacity: pagination.current_page > 1 ? 1 : 0.5,
                transition: 'all 0.14s',
                '&:hover': pagination.current_page > 1 ? { bgcolor: MP.tealLight } : undefined,
              }}
            >
              ← Prev
            </Box>
            <Box
              onClick={() => pagination.current_page < pagination.last_page && onPageChange(pagination.current_page + 1)}
              sx={{
                fontFamily: MONO,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: pagination.current_page < pagination.last_page ? MP.tealDark : MP.textMuted,
                bgcolor: MP.white,
                border: `1px solid ${pagination.current_page < pagination.last_page ? MP.tealMid : MP.border}`,
                borderRadius: '6px',
                px: 1.5,
                py: 0.75,
                cursor: pagination.current_page < pagination.last_page ? 'pointer' : 'not-allowed',
                opacity: pagination.current_page < pagination.last_page ? 1 : 0.5,
                transition: 'all 0.14s',
                '&:hover': pagination.current_page < pagination.last_page ? { bgcolor: MP.tealLight } : undefined,
              }}
            >
              Next →
            </Box>
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

export default EdosResultsList;
